import { useRef, useState } from "react";
import React from "react";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import { withStyles } from "@mui/styles";
import { useEffect } from "react";
import { useAppDispatch } from "hooks/redux";
import { snackbarRequest } from "shared/molecules/snackbar/snackbar-slice";
// GCP IMPORTS
import "./styles.css";
import GCPUploadService from "utils/gcp/upload";
const sanitize = require("sanitize-filename");

type UploadProps = {
  onChange: any;
  fields: any;
  getValues: any;
  classes: any;
  value: any;
  className: string;
  watch?: any;
  errors: any;
  indexNumber?: any;
};

const styles = {
  snackbarStyleViaContentProps: {
    backgroundColor: "red",
    color: "white",
  },
  snackbarStyleViaNestedContent: {
    backgroundColor: "green",
    color: "white",
  },
};

const Upload = ({
  onChange,
  fields,
  getValues,
  classes,
  className,
  value,
  watch,
  errors,
  indexNumber,
}: UploadProps) => {
  const [showLoading, setShowLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const dropRef = useRef<any>(null);
  const dispatch = useAppDispatch();
  const [images, setImages] = useState<any>([]);
  const [imagesNamesList, setImagesNamesList] = useState<string[]>([]);

  const [dragging, setDragging] = useState(false);

  // ---------------------- GCP PATH GENERATION ----------------------
  const getPath = () => {
    const values = getValues();
    let path = "others";

    if (fields.feature === "assignments") {
      path = `assignments/${values.class_id}/course/${values.subject_id}/chapter`;
    } else if (fields.feature === "schedule") {
      path = `schedule/${values.class_id}/${values.course_id}/`;
    } else if (fields.feature === "term-exams") {
      path = `descriptive-exam/${values.class_id}`;
    } else if (fields.feature === "learn") {
      path = `learn/resources/${values.class_id}/${
        values.course_id || "course"
      }/${values.subject_id || "subject"}/${
        values.topic_id || values.chapter_id || "chapter"
      }`;
    } else if (fields.feature === "build-test") {
      path = `build-test`;
    } else if (fields.feature === "announcements") {
      path = `announcements`;
    } else if (fields.feature === "fun-sheet") {
      path = `fun-sheet`;
    } else if (fields.feature === "magazine") {
      path = `magazine`;
    } else if (fields.feature === "miscellaneous") {
      path = `instructions`;
    }
    return path;
  };

  // Map component feature names to GCP feature keys
  const getGCPFeature = ()=> {
    const mapping= {
      assignment: "assignment",
      assignments: "assignments-ncms",
      "term-exams": "term-exams-ncms",
      learn: "learn-ncms",
      schedule: "monthly-schedule-ncms",
      "monthly-schedule": "monthly-schedule-ncms",
      "fun-sheet": "miscellaneous-ncms",
      miscellaneous: "miscellaneous-ncms",
      announcements: "announcement-ncms",
      "build-test": "miscellaneous-ncms",
      magazine: "activity-magzine-ncms",
    };
    return mapping[fields.feature] || "assignments-ncms";
  };

  // ---------------------- GCP MULTI-FILE UPLOAD ----------------------
  const handleCapture = async (
    event: React.ChangeEvent<HTMLInputElement> | any
  ) => {
    const { dataTransfer, target } = event;
    let files = dataTransfer ? dataTransfer.files : target.files;

    if (!files || files.length === 0) return;

    // Generate filenames (sanitized/original based on field config)
    let nxtFileNames: any = Object.values(files).map((item: any, idx) => {
      if (fields.isSameFileName) {
        return sanitize(item.name);
      }
      // Replace spaces with underscores like original
      return item.name.split(" ").join("_");
    });

    // Validation
    let fileSize = fields.size || 50;
    let extensionType = files[0].name.substring(
      files[0].name.lastIndexOf(".") + 1
    );
    const sizes = Object.values(files).map((item: any) => item.size);
    const totalSize = sizes.reduce((pre, curr) => pre + curr, 0);
    let isFileSizeOverflow = !(Math.ceil(totalSize / 1000000) <= fileSize);

    if (isFileSizeOverflow || !fields.fileType.includes(`.${extensionType}`)) {
      setShowLoading(false);
      let message = isFileSizeOverflow
        ? `File size is more than ${fileSize}MB, Please select valid file`
        : "Please select valid file type";
      dispatch(snackbarRequest({ message: message, type: "error" }));
      return;
    }

    // ---------------- UPLOAD START ----------------
    try {
      setShowLoading(true);

      const gcpFeature = getGCPFeature();
      const dynamicPath = getPath();

      let finalResult: any = [];
      let fileUrls: string[] = [];

      // Upload all files
      for (let i = 0; i < nxtFileNames.length; i++) {
        const file = files[i];
        const fileName = nxtFileNames[i];

        const fullFilePath = [dynamicPath, fileName]
          .filter(Boolean)
          .join("/");

        const uploadResult = await GCPUploadService.generateSignedUrlAndUpload(
          gcpFeature,
          fullFilePath,
          file,
          file.type
        );

        if (uploadResult.success && uploadResult.signedUrlResponse) {
          const publicUrl =
            uploadResult.signedUrlResponse.file_url ||
            uploadResult.signedUrlResponse.signed_url.split("?")[0];

          finalResult.push(uploadResult);
          fileUrls.push(publicUrl);
        } else {
          throw new Error(
            uploadResult.error || `Upload failed for file ${i + 1}`
          );
        }
      }

      // ---------- SUCCESS UI ----------
      if (finalResult.length > 0) {
        setShowLoading(false);
        dispatch(snackbarRequest({ message: "Successfully Uploaded" }));

        setImagesNamesList([...imagesNamesList, ...nxtFileNames]);

        if (fileUrls) {
          // Only use new fileUrls, not previous values
          setImages([...fileUrls]);
        }
      }

      // Clear input
      if (event.target) {
        event.target.value = null;
      }
    } catch (err: any) {
      setShowLoading(false);
      console.log(`Upload error: ${err}`);
      dispatch(
        snackbarRequest({
          message: err?.message || "Upload failed",
          type: "error",
        })
      );
    }
  };

  useEffect(() => {
    const mainData = images;
    const val =
      mainData &&
      mainData.length > 0 &&
      mainData.map((item, index) => {
        const nArr = item && item.length > 0 && item?.split(/\/|\.|-/);
        return {
          image_url: item,
          image_name: `${nArr[nArr.length - 3]}_${nArr[nArr.length - 2]}`,
          sort_order: Number(nArr[nArr.length - 2]),
          is_hilight: false,
        };
      });
    onChange(val);
  }, [images]);

  const handleValidate = (event) => {
    handleCapture(event);
  };

  //==================== DRAG & DROP HANDLERS ====================

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragIn = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setDragging(true);
    }
  };

  const handleDragOut = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleCapture(e);
      e.dataTransfer.clearData();
    }
  };

  useEffect(() => {
    let drop = dropRef.current;

    if (drop) {
      drop.addEventListener("dragenter", handleDragIn);
      drop.addEventListener("dragleave", handleDragOut);
      drop.addEventListener("dragover", handleDrag);
      drop.addEventListener("drop", handleDrop);

      return () => {
        drop.removeEventListener("dragenter", handleDragIn);
        drop.removeEventListener("dragleave", handleDragOut);
        drop.removeEventListener("dragover", handleDrag);
        drop.removeEventListener("drop", handleDrop);
      };
    }
  }, []);

  return (
    <>
      {showLoading && (
        <Backdrop sx={{ color: "#fff", zIndex: 1301 }} open={showLoading}>
          <CircularProgress color="inherit" />
        </Backdrop>
      )}
      <div ref={dropRef}>
        <div className={`drop-area ${dragging && "drag-area"}`}>
          <input
            type="file"
            onChange={handleCapture}
            multiple
            name="file"
            id="file"
            ref={fileInputRef}
            accept={fields.acceptTypes}
          />
          <div className="text-area">
            <span>+</span>
            <span>Add/Drag</span>
            <span>images</span>
            <span>here</span>
          </div>
        </div>
      </div>
    </>
  );
};

export default withStyles(styles)(Upload);